import {
    AccordionDetails,
    AccordionSummary,
    Divider,
    InputAdornment,
    Typography,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectIsHyperSearchMenu } from '@/state/general/generalSelectors';
import { setIsAsideMenu, setIsHyperSearch } from '@/state/general/generalSlice';
import Drawer from '@mui/material/Drawer';
import Header from '@/components/Hypertracker/Header';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import HyperTrackerFooter from '@/components/Hypertracker/HyperTrackerFooter';
import {
    AccordionStyled,
    FooterWrapper,
    MenuContent,
} from '@/components/Hypertracker/AsideMenu/AsideMenuStyled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { storageKey } from '@/components/Hypertracker/HyperSearch/hypersearchTypes';
import {
    PopperContent,
    SearchMobileWrapper,
} from '@/components/Hypertracker/HyperSearch/HyperSearchStyled';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import Popper from '@mui/material/Popper';
import LastSearchList from '@/components/Hypertracker/HyperSearch/LastSearchList';
import { pnlSegments, SegmentId, SelectOption, sizeSegments } from '@/lib/types/hypertrackerTypes';
import {
    segmentIdToLabel,
    segmentsCriteriaLabels,
    segmentsLabels,
} from '@/lib/constants/hyperTrackerConstants';
import { ROUTES } from '@/lib/constants';
import { useLocation, useNavigate } from 'react-router';
import {
    useUnifiedSearch,
    MIN_CHARS_FE_LOCAL,
} from '@/components/Hypertracker/UnifiedSearch/useUnifiedSearch';
import {
    saveLastSearch,
    useSearchRowNavigate,
} from '@/components/Hypertracker/UnifiedSearch/useSearchRowNavigate';
import UnifiedSearchDropdown from '@/components/Hypertracker/UnifiedSearch/UnifiedSearchDropdown';

const HyperSearchMobile = () => {
    const theme = useTheme();
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const container = window !== undefined ? () => window.document.body : undefined;
    const [expanded, setExpanded] = useState<string | false>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const listRef = useRef<HTMLUListElement | null>(null);
    const isSearchVisible = useSelector((state: RootState) => selectIsHyperSearchMenu(state));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (isSearchVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isSearchVisible]);

    useEffect(() => {
        if (isSearchVisible) {
            setSearchText('');
        }

        return () => {
            setSearchText('');
        };
    }, [isSearchVisible]);

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const { entries, isLoadingRemote } = useUnifiedSearch(searchText);

    const closeSearchMenu = () => {
        setSearchText('');
        setFocusedIndex(-1);
        dispatch(setIsHyperSearch(false));
        dispatch(setIsAsideMenu(false));
    };

    const { onResultClick } = useSearchRowNavigate(closeSearchMenu);

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const isOpen = useMemo(() => {
        return searchText.trim().length >= MIN_CHARS_FE_LOCAL;
    }, [searchText]);

    const lastSearch = useMemo(() => {
        const storageData = localStorage.getItem(storageKey);
        return storageData ? JSON.parse(storageData) : null;
    }, [location]);

    const sizeSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return sizeSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    const pnlSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return pnlSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    const onSegmentClick = (segment) => {
        dispatch(setIsAsideMenu(false));
        dispatch(setIsHyperSearch(false));
        navigate(`${ROUTES.HYPERTRACKER_SEGMENTS}/${segmentIdToLabel[+segment]}`);
    };

    return (
        <Drawer
            container={container}
            variant="temporary"
            open={isSearchVisible}
            onClose={() => dispatch(setIsHyperSearch(false))}
            ModalProps={{
                keepMounted: false, // Better open performance on mobile if true.
            }}
            sx={{
                display: { sm: 'flex', md: 'none' },
                '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: '100%',
                    backgroundColor: theme.palette.backgrounds.bg0,
                    color: '#fff',
                    fontFamily: 'Manrope',
                    backgroundImage: 'none',
                },
            }}
        >
            <Header />
            <Divider
                orientation="horizontal"
                sx={{
                    margin: 0,
                    width: '100%',
                    height: '1px',
                    borderColor: (theme) => theme.palette.backgrounds.bg4,
                    borderWidth: '1px',
                }}
            />
            <MenuContent>
                <SearchMobileWrapper id={'search-input-mobile'}>
                    <TextField
                        label={null}
                        value={searchText}
                        onChange={onInputChange}
                        variant={'outlined'}
                        fullWidth={true}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position={'start'}>
                                        <SearchIcon
                                            sx={{
                                                height: '16px',
                                                width: 'auto',
                                                path: { fill: theme.palette.customText.text1 },
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                                placeholder: t('hypersearch.search_placeholder'),
                                autoComplete: 'off',
                                autoCapitalize: 'off',
                                autoCorrect: 'off',
                            },
                        }}
                        sx={{
                            '& label.Mui-focused': {
                                color: 'red',
                                background: 'red',
                            },
                            '& .MuiInput-underline:after': {
                                background: '#B2BAC2',
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'transparent',
                                    borderWidth: '1px',
                                    borderRadius: '28px',
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.palette.hyperLiquidColor,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.hyperLiquidColor,
                                },
                                borderRadius: '28px',
                                backgroundColor: theme.palette.backgrounds.bg3,
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: theme.palette.customText.text1,
                                opacity: 1,
                                fontSize: '12px',
                            },
                            '& .MuiInputBase-root': {
                                height: '48px',
                            },
                        }}
                    />
                    <Popper
                        open={isOpen}
                        placement="bottom"
                        anchorEl={isOpen ? document.querySelector('#search-input-mobile') : null}
                        sx={{
                            zIndex: 10_000,
                        }}
                        modifiers={[
                            {
                                name: 'flip',
                                enabled: false,
                                options: {
                                    altBoundary: false,
                                    rootBoundary: 'viewport',
                                    padding: 8,
                                },
                            },
                            {
                                name: 'preventOverflow',
                                enabled: false,
                                options: {
                                    altAxis: false,
                                    altBoundary: false,
                                    tether: false,
                                    rootBoundary: 'document',
                                    padding: 8,
                                },
                            },
                        ]}
                    >
                        <PopperContent sx={{ width: 'unset', maxWidth: '100%', minWidth: '50px' }}>
                            <UnifiedSearchDropdown
                                entries={entries}
                                searchText={searchText}
                                isLoadingRemote={isLoadingRemote}
                                focusedIndex={focusedIndex}
                                setFocusedIndex={setFocusedIndex}
                                listRef={listRef}
                                onResultClick={onResultClick}
                            />
                        </PopperContent>
                    </Popper>
                    <LastSearchList
                        lastSearch={lastSearch}
                        onSaveSearchClick={(searchItem) => {
                            saveLastSearch(searchItem);
                            closeSearchMenu();
                        }}
                    />
                </SearchMobileWrapper>
                <Divider
                    orientation="horizontal"
                    sx={{ margin: 0, width: '100%', height: '1px' }}
                />
                <AccordionStyled
                    disableGutters
                    elevation={0}
                    expanded={expanded === 'panel3'}
                    onChange={handleChange('panel3')}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel3bh-content"
                        id="panel3bh-header"
                    >
                        <Typography component="span" variant={'p14'} color={'common.white'}>
                            {t('profile_menu_drawer.cohorts')}
                        </Typography>
                    </AccordionSummary>
                    <Divider
                        orientation="horizontal"
                        sx={{ margin: 0, width: '100%', height: '1px' }}
                    />
                    <AccordionDetails>
                        {sizeSegmentsOptions.map((item) => {
                            return (
                                <Typography
                                    key={item.value}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'whiteTransparent',
                                        },
                                    }}
                                    lineHeight={'32px'}
                                    onClick={() => onSegmentClick(item.value)}
                                >
                                    <Typography variant="p12" color="customText.text4">
                                        {item.label}
                                    </Typography>
                                    &nbsp;
                                    <Typography variant="p12" color="customText.text1">
                                        {t(segmentsCriteriaLabels[item.value as SegmentId])}
                                    </Typography>
                                </Typography>
                            );
                        })}

                        <Typography
                            sx={{
                                borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
                                borderTop: '1px solid rgba(255, 255, 255, 0.10)',
                            }}
                            marginTop={'12px'}
                            component={'p'}
                            width={'100%'}
                            lineHeight={'32px'}
                            variant={'p12'}
                            color="customText.text1"
                        >
                            {t('header.pnl')}
                        </Typography>
                        {pnlSegmentsOptions.map((item) => {
                            return (
                                <Typography
                                    key={item.value}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'whiteTransparent',
                                        },
                                    }}
                                    lineHeight={'32px'}
                                    onClick={() => onSegmentClick(item.value)}
                                >
                                    <Typography variant="p12" color="customText.text4">
                                        {item.label}
                                    </Typography>
                                    &nbsp;
                                    <Typography variant="p12" color="customText.text1">
                                        {t(segmentsCriteriaLabels[item.value as SegmentId])}
                                    </Typography>
                                </Typography>
                            );
                        })}
                    </AccordionDetails>
                </AccordionStyled>
            </MenuContent>
            <FooterWrapper>
                <HyperTrackerFooter />
            </FooterWrapper>
        </Drawer>
    );
};

export default HyperSearchMobile;
